import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ErrorPanel } from './ErrorPanel';
import { PageShell } from './PageShell';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  failed: boolean;
}

/**
 * The last line between a render fault and a white screen.
 *
 * React unmounts the whole tree when a render throws and nothing catches it,
 * so a single bad component takes the entire call with it: video, dock, the
 * lot, replaced by a blank page with no way back. That is the worst possible
 * shape for the failure, because it also removes every control the person
 * could have used to recover.
 *
 * This does not hide the fault. The error still reaches the console, and
 * componentDidCatch logs it deliberately so that a report of "the screen went
 * white" arrives with something attached to it. What changes is what the
 * person is left holding: a panel that names the problem and offers a reload,
 * rather than nothing at all.
 *
 * A class on purpose. Error boundaries have no hook equivalent; this is the
 * one part of React that still requires one.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Deliberate diagnostic. A white screen is the one failure users report
    // with no detail whatsoever, and the component stack is what turns that
    // report into something findable.
    console.error(
      `[app] render failed: ${error.name}: ${error.message}`,
      info.componentStack,
    );
  }

  render() {
    if (!this.state.failed) return this.props.children;

    return (
      <PageShell>
        <ErrorPanel
          title="Something went wrong"
          message="This page stopped responding. Reloading should bring you back to where you were."
          onRetry={() => window.location.reload()}
          // Clearing the flag would re-render the same broken tree from the
          // same state and fail again, so recovery is a reload rather than a
          // retry in place. The way out that is not a reload goes home, which
          // is a different route and therefore a different tree.
          onBack={() => {
            window.location.href = '/';
          }}
        />
      </PageShell>
    );
  }
}
